import { Capacitor, registerPlugin } from '@capacitor/core';

export const PREMIUM_PRODUCT_IDS = {
  monthly: 'straiv_premium_monthly',
  yearly: 'straiv_premium_yearly',
} as const;

export type PremiumProductId =
  (typeof PREMIUM_PRODUCT_IDS)[keyof typeof PREMIUM_PRODUCT_IDS];

export type StoreProduct = {
  id: string;
  displayName: string;
  description: string;
  displayPrice: string;
  price: number;
  currencyCode?: string;
  periodUnit?: string;
  periodValue?: number;
};

/** e.g. yearly $59.99 → "$5.00" for “≈ X / month when billed annually” */
export function formatYearlyAsMonthlyPrice(product: StoreProduct | undefined | null): string | null {
  if (!product || !(product.price > 0)) return null;
  const perMonth = product.price / 12;
  const currency = product.currencyCode?.trim();
  try {
    if (currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(perMonth);
    }
  } catch {
    // fall through
  }
  // Fallback: reuse currency symbol/prefix from displayPrice when possible
  const match = product.displayPrice?.match(/^([^\d-]*)/);
  const prefix = match?.[1] ?? '';
  return `${prefix}${perMonth.toFixed(2)}`;
}

export type StoreTransaction = {
  productId: string;
  transactionId: string;
  originalTransactionId: string;
  purchaseDate: string;
  expiresDate?: string;
  revocationDate?: string;
  jwsRepresentation: string;
  environment?: string;
};

type StraivPurchasesPlugin = {
  getProducts(options?: { productIds?: string[] }): Promise<{ products: StoreProduct[] }>;
  purchase(options: { productId: string }): Promise<{
    success: boolean;
    cancelled?: boolean;
    pending?: boolean;
    transaction?: StoreTransaction;
  }>;
  restore(): Promise<{ entitlements: StoreTransaction[] }>;
  getEntitlements(): Promise<{ entitlements: StoreTransaction[] }>;
  addListener(
    eventName: 'transactionUpdated',
    listener: (data: { transaction: StoreTransaction }) => void,
  ): Promise<{ remove: () => void }>;
};

const Purchases = registerPlugin<StraivPurchasesPlugin>('StraivPurchases');

export function isPurchasesAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export async function getPremiumProducts(): Promise<StoreProduct[]> {
  if (!isPurchasesAvailable()) return [];
  const { products } = await Purchases.getProducts({
    productIds: [PREMIUM_PRODUCT_IDS.monthly, PREMIUM_PRODUCT_IDS.yearly],
  });
  return products ?? [];
}

export async function purchasePremium(productId: PremiumProductId | string) {
  if (!isPurchasesAvailable()) {
    throw new Error('In-app purchases are only available on iOS.');
  }
  return Purchases.purchase({ productId });
}

export async function restorePremiumPurchases() {
  if (!isPurchasesAvailable()) {
    throw new Error('Restore is only available on iOS.');
  }
  return Purchases.restore();
}

export async function getPremiumEntitlements() {
  if (!isPurchasesAvailable()) return [];
  const { entitlements } = await Purchases.getEntitlements();
  return entitlements ?? [];
}

export { Purchases as StraivPurchasesNative };
